import React, { useEffect, useMemo, useState } from 'react';
import { Plate, SubHeader, Text } from 'features/shared/ui/Page';
import Spacer from 'features/shared/Spacer';
import SwitchTabs, { BluePillsTab, SwitchTabsType } from 'features/shared/SwitchTabs';
import { SchoolSelector, MunicipalitySelector } from './ChartDataSelector';
import Chart from './Chart';
import ReportDownloadBar from 'features/shared/DownloadBar/ReportDownloadBar';
import { SchoolsApi } from 'api';
import { Api } from 'api/cabmun-api-public';
import { ChartData } from './models';
import useUserData from 'hooks/useUserData';
import FileSaver from 'file-saver';
import { useClickStreamContext } from 'features/shared/ClickStream';

enum TabId {
  InteractiveTasks = 'interactive',
  AutocheckTasks = 'automatic',
}

const tabs = [
  { id: TabId.InteractiveTasks, label: 'Интерактивные задания' },
  { id: TabId.AutocheckTasks, label: 'Задания с автопроверкой' },
];

const grades = {
  primary: '1–4 КЛАССЫ',
  middle: '5–9 КЛАССЫ',
  high: '10–11 КЛАССЫ',
};

const Component = (props: { isRegion: boolean }) => {
  const { logPageEvent } = useClickStreamContext();

  const [tab, setTab] = useState(tabs[0]);
  const [filter, setFilter] = useState<{ value: number; label: string } | null>(null);
  const [statistics, setStatistics] = useState<Api.ActivityStatisticsBySchoolSubjects>();

  const isFilterSelected = !!(filter && filter.value !== -1);

  const { regionName, department } = useUserData() || {};
  const fetch = async () => {
    const result = await SchoolsApi.getActivityStatisticsBySchoolSubjects();
    setStatistics(result);
  };

  useEffect(() => {
    fetch();
  }, []);

  const [pendingDownload, setPendingDownload] = useState(false);
  const downloadReport = () => {
    logPageEvent({
      eventType: 'schools_active_by_service_click_download_report',
      eventInfo: 'Школы|Активность на сервисах: Нажал скачать отчет',
    });
    if (pendingDownload) return;
    setPendingDownload(true);
    SchoolsApi.getActivityStatisticsBySchoolSubjectsXlsx()
      .then((response: any) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        FileSaver.saveAs(blob, `Статистика по сервисам.xlsx`);
        setPendingDownload(false);
      })
      .catch(() => {
        setPendingDownload(false);
      });
  };

  const services: ChartData[] = useMemo(() => {
    if (!statistics) return [];
    const filteredByTask = statistics.detailed_data.filter((el) => el.task_type === tab.id);
    const totalFilteredByTask = statistics.total.filter((el) => el.task_type === tab.id);
    return Object.keys(grades).map(
      (grade): ChartData =>
        ({
          classes: grades[grade as keyof typeof grades],
          subjects: filteredByTask
            .filter((el) => !!el.school_subject && !!el.students_count)
            .map((el) => el.school_subject)
            .filter((el, i, arr) => arr.indexOf(el) === i),
          ...totalFilteredByTask
            .filter((el) => el.group === grade)
            .reduce(
              (acc, el) => ({ ...acc, [el.school_subject + (isFilterSelected ? '_total' : '')]: el.students_count }),
              {},
            ),
          ...(isFilterSelected
            ? filteredByTask
                .filter((el) => el.group === grade)
                .filter((el) => el[props.isRegion ? 'municipality_id' : 'school_id'] === filter?.value)
                .reduce((acc, el) => ({ ...acc, [el.school_subject]: el.students_count }), {})
            : {}),
        } as ChartData),
    );
  }, [tab, filter, statistics]);

  return (
    <Plate>
      <SubHeader>Активность учеников на сервисах Учи.ру</SubHeader>
      <Spacer size={16} />
      <Text>
        В статистике учитываются школьники, выполнившие задания в интерактивном формате
        <br /> или с автоматической проверкой
      </Text>
      <Spacer size={24} />
      <ReportDownloadBar title={'Скачать'} onClick={downloadReport}>
        Скачайте отчет активности школьников на Учи.ру
      </ReportDownloadBar>
      <Spacer size={32} />
      <SwitchTabs
        values={tabs}
        value={tab}
        tabRenderer={(props) => <BluePillsTab {...props} />}
        handleChange={(value) => {
          setTab(value);
          logPageEvent({
            eventType: 'schools_active_by_service_select_tab_' + value.id,
            eventInfo: 'Школы|Активность на сервисах: Перешел на вкладку ' + value.label,
          });
        }}
        type={SwitchTabsType.pills}
      />
      <Spacer size={43} />
      {props.isRegion ? (
        <MunicipalitySelector
          value={filter}
          setValue={(value) => {
            setFilter(value);
            if (filter) {
              logPageEvent({
                eventType: 'schools_active_by_service_change_municipality_filter',
                eventInfo: 'Школы|Активность на сервисах: выбрал значение в фильтре по муниципалитету',
              });
            }
          }}
          regionName={regionName}
        />
      ) : (
        <SchoolSelector
          value={filter}
          setValue={(value) => {
            setFilter(value);
            if (filter) {
              logPageEvent({
                eventType: 'schools_active_by_service_change_school_filter',
                eventInfo: 'Школы|Активность на сервисах: выбрал значение в фильтре по школам',
              });
            }
          }}
          municipalityName={department}
        />
      )}
      <Spacer size={32} />
      {tab.id === TabId.InteractiveTasks ? (
        <Text>
          Количество учеников, которые самостоятельно выполнили интерактивные задания <br />
          на платформе с начала учебного года и на момент просмотра графика.
        </Text>
      ) : (
        <Text>
          Количество заданий с автопроверкой, которые выдали учителя и выполнили ученики <br />с начала учебного года и
          на момент просмотра графика.
        </Text>
      )}

      <Spacer size={16} />
      <Chart yTitle="Ученики" data={services} showTotal={isFilterSelected} />
    </Plate>
  );
};

export default Component;
